import { X, ArrowRight, ArrowLeft, ShieldAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { KIND_BADGE, SENSITIVITY_DOT } from "@/lib/semantics";
import { SEMANTICS } from "@/constants/testIds";

export function NodePanel({ node, edges, onClose, onSelectNode }) {
  if (!node) return null;
  const outgoing = edges.filter((e) => e.from_fqn === node.id);
  const incoming = edges.filter((e) => e.to_fqn === node.id);

  return (
    <aside
      data-testid={SEMANTICS.nodePanel}
      className="absolute right-0 top-0 z-20 flex h-full w-[340px] flex-col border-l border-border/60 bg-background/95 font-mono backdrop-blur"
    >
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{node.schema}</div>
          <div className="truncate text-sm font-semibold">{node.table}</div>
        </div>
        <button
          data-testid={SEMANTICS.nodePanelClose}
          onClick={onClose}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <ScrollArea className="flex-1">
        <div className="space-y-5 p-4">
          {node.definition && (
            <p className="text-xs leading-relaxed text-muted-foreground">{node.definition}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            <span>{node.columns.length} columns</span>
            {node.row_count != null && <span>· {Number(node.row_count).toLocaleString()} rows</span>}
            {node.sensitivity && (
              <span className="inline-flex items-center gap-1">
                · <span className={`h-1.5 w-1.5 rounded-full ${SENSITIVITY_DOT[node.sensitivity] || "bg-muted-foreground"}`} />
                {node.sensitivity}
              </span>
            )}
          </div>

          <div>
            <div className="mb-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Columns</div>
            <div className="space-y-1.5">
              {node.columns.map((c) => (
                <div key={c.name} data-testid={SEMANTICS.nodeColumn} className="rounded-md border border-border/50 bg-card/40 px-2.5 py-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs font-medium">{c.name}</span>
                    <span className="shrink-0 text-[10px] text-muted-foreground">{c.data_type}</span>
                  </div>
                  {c.definition && <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">{c.definition}</p>}
                  {c.sensitivity === "Restricted" && (
                    <span className="mt-1 inline-flex items-center gap-1 text-[9px] text-red-400">
                      <ShieldAlert className="h-3 w-3" /> Restricted
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {(outgoing.length > 0 || incoming.length > 0) && (
            <div>
              <div className="mb-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Relationships</div>
              <div className="space-y-1.5">
                {outgoing.map((e) => (
                  <EdgeRow key={e.id} edge={e} dir="out" me={node.id} onSelectNode={onSelectNode} />
                ))}
                {incoming.map((e) => (
                  <EdgeRow key={e.id} edge={e} dir="in" me={node.id} onSelectNode={onSelectNode} />
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </aside>
  );
}

function EdgeRow({ edge, dir, me, onSelectNode }) {
  const other = me === edge.from_fqn ? edge.to_fqn : edge.from_fqn;
  const Icon = dir === "out" ? ArrowRight : ArrowLeft;
  return (
    <button
      onClick={() => onSelectNode(other)}
      className="flex w-full items-center gap-2 rounded-md border border-border/40 px-2.5 py-1.5 text-left text-[11px] hover:border-accent/50 hover:bg-muted/40"
    >
      <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="truncate text-foreground">{other}</span>
      <Badge variant="outline" className={`ml-auto shrink-0 rounded-full px-1.5 py-0 text-[9px] ${KIND_BADGE[edge.kind] || ""}`}>
        {edge.kind}
      </Badge>
    </button>
  );
}
